import { Fragment } from 'react';
import { connect } from 'react-redux';
import {
  ApplicationState,
  CaseSettingsState,
  AppState,
  CaseSettingsActionCreators
} from '../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { AppContext, ApplicationContext } from '../../context/Contexts';
import { ITranslatorService } from '../../services/Interfaces/ITranslatorService';
import { RouteComponentProps } from 'react-router';
import { ScaleLoader } from 'react-spinners';
import {
  GetSuppliersByStatusRequestDto,
  WorkflowSectionStepForm,
  WorkflowSupplier
} from '../../interfaces/Workflow';
import { isNullOrUndefined } from 'util';
import { ICaseService } from '../../services/Interfaces/ICaseService';
import { FormHelpers } from '../../helpers/forms/FormHelpers';
import 'react-dropdown-tree-select/dist/styles.css';
import { Button, Checkbox, CircularProgress, Grid, Typography } from '@material-ui/core';
import { AppUser } from '../../interfaces/AppUser';
import { FormControlLabel } from '@material-ui/core';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { CaseStatusCodeEnum, ReferentialCode } from '../../helpers/Constants';
import Referentials from '../../helpers/Referentials.json';
import { IReferentialService } from '../../services/Interfaces/IReferentialService';
import { IReferential } from '../../interfaces/IReferential';
import {
  CasePartSelection,
  ChangeCaseStatusBasedCodeRequest,
  ChangeCaseStatusRequest,
  CreatePackageOfferRequestDto
} from '../../interfaces/Case';
import { Autocomplete } from '@material-ui/lab';
import React from 'react';
import ArrowBackTwoToneIcon from '@material-ui/icons/ArrowBackTwoTone';
import { Organization } from '../../interfaces/Organization';
import { IWorkflowService } from '../../services/Interfaces/IWorkflowService';

interface ICasePartSelectionFormState {
  isLoading: boolean;
  hasRights: boolean;
  executing: boolean;
  partOptions: IReferential[];
  partSelectionOptions: IReferential[];
  selectedPartSelectionOption: IReferential | null;
  casePartSelection: CasePartSelection;
  isContinueLoading: boolean;
}

export interface ExternalCaseDetailsCarInServiceFormProps {
  workflowForm: WorkflowSectionStepForm;
}

type ICasePartSelectionFormProps = ExternalCaseDetailsCarInServiceFormProps & {
  caseSettingsState: CaseSettingsState;
  appState: AppState;
} & typeof CaseSettingsActionCreators &
  ProviderContext &
  RouteComponentProps<{ id: string }>;

class CasePartSelectionForm extends React.PureComponent<
  ICasePartSelectionFormProps,
  ICasePartSelectionFormState
> {
  private translatorService!: ITranslatorService;
  private caseService!: ICaseService;
  private appReferentialService!: IReferentialService;
  private workflowService!: IWorkflowService;

  static contextType = ApplicationContext;
  state = {
    isLoading: false,
    hasRights: false,
    isUserHidden: false,
    executing: false,
    partOptions: [],
    partSelectionOptions: [],
    selectedPartSelectionOption: null,
    casePartSelection: {
      id: 0,
      caseId: this.props.caseSettingsState.case!.id,
      partSelectionOption: null,
      partSelectionOptionId: null,
      casePartSelectionOptions: []
    } as CasePartSelection,
    isBackExecuting: false,
    isContinueLoading: false
  } as ICasePartSelectionFormState;

  public componentDidMount() {
    const caseSettings = this.props.caseSettingsState.caseSettings;
    const caseId = Number.parseInt(this.props.match.params.id);
    if (isNullOrUndefined(caseSettings) || Number.isNaN(caseId)) {
      return;
    }

    this.setState(
      {
        isLoading: true
      },
      async () => {
        await this.loadData();
      }
    );
  }

  loadData = async () => {
    const caseId = Number.parseInt(this.props.match.params.id);
    if (Number.isNaN(caseId)) {
      return;
    }
    const refPartOption = Referentials.referential.find(
      (item) => item.code === ReferentialCode.PartOption
    );
    const refPartSelectionOption = Referentials.referential.find(
      (item) => item.code === ReferentialCode.PartSelectionOption
    );

    const [casePartSelection, partOptions, partSelectionOptions] = await Promise.all([
      this.caseService.GetCurrentCasePartSelection(caseId),
      (await this.appReferentialService.Get(refPartOption!.baseUrl)).filter((x) => x.isActive),
      (
        await this.appReferentialService.Get(refPartSelectionOption!.baseUrl)
      ).filter((x) => x.isActive)
    ]);

    const hasRights = FormHelpers.HasRights(
      this.props.workflowForm.workflowFormPermissions,
      this.props.appState.appUser!,
      this.props.appState,
      this.props.caseSettingsState.case!.caseStatus.caseStatusId
    );

    this.setState({
      isLoading: false,
      hasRights: hasRights,
      partOptions: partOptions,
      partSelectionOptions: partSelectionOptions,
      casePartSelection: casePartSelection ? casePartSelection : this.state.casePartSelection
    });
  };

  submitForm = async (caseId: number, caseSectionStepFormId: number, appUser: AppUser | null) => {
    let newStatus = await this.caseService.SubmitForm(caseId, caseSectionStepFormId, appUser);

    if (newStatus === null) {
      newStatus = this.props.caseSettingsState.case!.caseStatus;
    }

    this.props.SetCaseStatus(newStatus);
    if (appUser !== null) {
      this.props.AddPartner(appUser!.hoId === null ? appUser!.organizationId : appUser!.hoId);
    }

    const hasRights = FormHelpers.HasRights(
      this.props.workflowForm.workflowFormPermissions,
      this.props.appState.appUser!,
      this.props.appState,
      newStatus.caseStatusId
    );
    this.setState({ hasRights: hasRights });
  };

  getInactiveWorkflowSuppliers = async () => {
    if (!this.props.caseSettingsState.caseSettings!.workflow.inactiveSupplierNotification) {
      return;
    }
    const inactiveSuppliers = await this.workflowService.GetWorkflowSuppliersByStatus({
      workflowId: this.props.caseSettingsState.caseSettings!.workflow.id,
      organizationId: this.props.caseSettingsState.case!.caseEvent.deliveryOrganizationId!,
      isActive: false,
      supplierId: null
    } as GetSuppliersByStatusRequestDto);

    if (inactiveSuppliers.length > 0) {
      this.props.enqueueSnackbar(
        (inactiveSuppliers.length == 1
          ? this.translatorService.Tranlate(
              'INACTIVE_SUPPLIERS_MSG_FIRST_PART_SINGLE',
              'Achizitia de la furnizorul'
            )
          : this.translatorService.Tranlate(
              'INACTIVE_SUPPLIERS_MSG_FIRST_PART_MULTIPLE',
              'Achizitia de la furnizorii'
            )) +
          ' ' +
          inactiveSuppliers.map((supplier) => supplier.supplierName).join(', ') +
          ' ' +
          (inactiveSuppliers.length == 1
            ? this.translatorService.Tranlate(
                'INACTIVE_SUPPLIERS_MSG_SECOND_PART_SINGLE',
                'este momentan blocată!'
              )
            : this.translatorService.Tranlate(
                'INACTIVE_SUPPLIERS_MSG_SECOND_PART_MULTIPLE',
                'sunt momentan blocate!'
              )),
        {
          variant: 'warning',
          autoHideDuration: 6000
        }
      );
    }
  };

  submitPartSelectionData = async (e: any) => {
    try {
      if (!this.state.casePartSelection.casePartSelectionOptions.length) {
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(
            'PLEASE_SELECT_PART_OPTION',
            'Va rugam selectati cel putin o optiune!'
          ),
          {
            variant: 'warning'
          }
        );
        return;
      }

      this.getInactiveWorkflowSuppliers();
      this.setState({ executing: true, isContinueLoading: true });

      const request = {
        caseId: this.props.caseSettingsState.case!.id,
        workflowId: this.props.caseSettingsState?.caseSettings?.workflow.id,
        serviceId: this.props.caseSettingsState.case!.caseEvent.deliveryOrganizationId!,
        countryId: this.props.appState.appUser?.organization?.countryId ?? 1,
        vehicleTypeId: this.props.caseSettingsState.case?.caseVehicle?.vehicleTypeId ?? null,
        jobTypeId: this.props.caseSettingsState.case?.jobTypeId ?? null,
        casePartSelection: this.state.casePartSelection
      } as CreatePackageOfferRequestDto;
      const response = await this.caseService.CreatePackageOffer(request);

      if (!response.success) {
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(
            response.gtEstimateResponseDto.error.errorInfo.description,
            response.gtEstimateResponseDto.error.errorInfo.description
          ),
          {
            variant: 'error',
            autoHideDuration: 6000
          }
        );
        return;
      }

      const item = await this.caseService.GetCurrentCasePartSelection(
        this.props.caseSettingsState.case!.id
      );
      if (item) {
        this.setState({
          casePartSelection: item
        });
      }

      if (response.excludedOptions.length > 0) {
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate('EXCLUDED_OPTIONS_MESSAGE', 'Optiuni excluse: ') +
            response.excludedOptions.map((x) => x.displayName).join(', '),
          {
            variant: 'warning',
            autoHideDuration: 6000
          }
        );
      }

      await this.submitForm(
        this.props.caseSettingsState.case!.id,
        this.props.workflowForm.id,
        null
      );

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (ex) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ executing: false, isContinueLoading: false });
    }
  };

  handleCancel = async () => {
    try {
      this.setState({ executing: true });
      const caseStatusRequest = {
        caseId: this.props.caseSettingsState.case!.id,
        statusId: CaseStatusCodeEnum.Closed
      } as ChangeCaseStatusRequest;

      await this.caseService.ChangeCaseStatus(caseStatusRequest);
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });

      this.props.history.push('/newCase');
    } catch (ex) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ executing: false });
    }
  };

  handleBack = async () => {
    try {
      this.setState({ executing: true });
      const caseStatusRequest = {
        caseId: this.props.caseSettingsState.case!.id,
        statusCode: CaseStatusCodeEnum.DRAFT
      } as ChangeCaseStatusBasedCodeRequest;

      const newStatus = await this.caseService.ChangeCaseStatusBasedCode(caseStatusRequest);
      this.props.SetCaseStatus(newStatus);
      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (ex) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ executing: false });
    }
  };

  handlePartSelectionOptionChange = (newValue: IReferential | null) => {
    this.setState({
      casePartSelection: {
        ...this.state.casePartSelection,
        partSelectionOption: newValue,
        partSelectionOptionId: newValue === null ? null : newValue.id
      }
    });
  };

  renderPartSelectionForm = () => {
    return (
      <ValidatorForm
        onSubmit={(e) => {
          this.submitPartSelectionData(e);
        }}
      >
        <Grid container spacing={0}>
          <>
            {this.state.partOptions.map((option) => {
              const isChecked = !!this.state.casePartSelection.casePartSelectionOptions.find(
                (x) => x.partOptionId === option.id
              );

              return (
                <Grid
                  item
                  xs={12}
                  key={option.id}
                  style={{ display: 'flex', justifyContent: 'flex-start' }}
                >
                  <FormControlLabel
                    disabled={!this.state.hasRights || this.state.executing}
                    className="case-part-selection-option"
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                          const updatedOptions = [
                            ...this.state.casePartSelection.casePartSelectionOptions
                          ];
                          if (e.target.checked) {
                            updatedOptions.push({
                              partOptionId: option.id
                            });
                          } else {
                            const index = updatedOptions.findIndex(
                              (x) => x.partOptionId === option.id
                            );
                            if (index > -1) updatedOptions.splice(index, 1);
                          }

                          this.setState((prev) => ({
                            casePartSelection: {
                              ...prev.casePartSelection,
                              casePartSelectionOptions: updatedOptions
                            }
                          }));
                        }}
                        name={option.displayName}
                        color="primary"
                      />
                    }
                    label={
                      <Typography
                        style={{
                          color: isChecked ? 'green' : 'black',
                          fontWeight: isChecked ? 'bold' : 'normal'
                        }}
                      >
                        {option.displayName}
                      </Typography>
                    }
                  />
                </Grid>
              );
            })}
          </>
          <Grid item xs={12}>
            <Autocomplete
              disabled={!this.state.hasRights}
              id="part-selection-option"
              className="mt-2 mb-2"
              options={this.state.partSelectionOptions}
              value={this.state.casePartSelection.partSelectionOption}
              onChange={(e: any, newValue: IReferential | null) =>
                this.handlePartSelectionOptionChange(newValue)
              }
              getOptionLabel={(option: IReferential) => option.displayName || ''}
              renderInput={(params) => (
                <TextValidator
                  {...params}
                  name="part-selection-option"
                  value={this.state.casePartSelection.partSelectionOption}
                  label={this.translatorService.Tranlate(
                    'PART_SELECTION_OPTION_LABEL',
                    'Opțiuni selecție automată piese'
                  )}
                  fullWidth
                  validators={['required']}
                  errorMessages={[
                    this.translatorService.Tranlate(
                      'VALIDATORS_REQUIRED',
                      'Campul este obligatoriu'
                    )
                  ]}
                />
              )}
            />
          </Grid>
        </Grid>
        {this.renderBottomButtons()}
      </ValidatorForm>
    );
  };

  renderBottomButtons = () => {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        className="mt-3 mb-3"
      >
        <Button
          className="m-2"
          variant="contained"
          color="primary"
          type="submit"
          disabled={!this.state.hasRights || this.state.executing}
        >
          {this.state.isContinueLoading && (
            <CircularProgress size={16} style={{ marginRight: 5 }} />
          )}
          {this.translatorService.Tranlate('CASE_PART_SELECTION_CONTINUE_BUTTON', 'Continua')}
        </Button>

        <Button
          style={{
            background: 'transparent',
            boxShadow: 'none',
            textTransform: 'none',
            padding: 0,
            minWidth: 'auto',
            fontSize: '0.75rem',
            color: 'var(--primary)',
            textDecoration: 'underline'
          }}
          onClick={this.handleCancel}
          className="link-button"
          disabled={!this.state.hasRights || this.state.executing}
        >
          {this.translatorService.Tranlate('CANCEL_BUTTON_LABEL', 'Anulează')}
        </Button>
      </div>
    );
  };

  renderBackButton = () => {
    return (
      <Grid container spacing={0} style={{ marginBottom: '10px' }}>
        <Grid item xs={12}>
          <Button
            className="shadow-none custom-button"
            variant="outlined"
            color="primary"
            size="small"
            disabled={!this.state.hasRights || this.state.executing}
            startIcon={<ArrowBackTwoToneIcon />}
            onClick={this.handleBack}
          >
            {this.translatorService.Tranlate('CASE_PART_CAR_DATA_BACK_BUTTON', 'Date masina')}
          </Button>
        </Grid>
      </Grid>
    );
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.caseService = (this.context as AppContext).caseService;
    this.appReferentialService = (this.context as AppContext).referentialService;
    this.workflowService = (this.context as AppContext).workflowService;

    return (
      <Fragment>
        <div className="d-flex flex-row text-center flex-wrap justify-content-center">
          <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
        </div>
        {!this.state.isLoading ? (
          <div className="m-2 ml-4 mr-4">
            {this.renderBackButton()}
            {this.renderPartSelectionForm()}
          </div>
        ) : null}
      </Fragment>
    );
  }
}

const mergeProps = (
  stateProps: any,
  dispatchProps: any,
  ownProps: ExternalCaseDetailsCarInServiceFormProps
) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps
});

export default connect(
  (state: ApplicationState) => ({
    caseSettingsState: state.caseSettings,
    appState: state.app
  }),
  CaseSettingsActionCreators,
  mergeProps
)(withSnackbar(CasePartSelectionForm as any));
